import { useMemo, useState, VFC } from 'react';

import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import FieldsList from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldsList';
import { useWorkflowActionMeta } from 'dashboard/components/WorkflowEditor/WorkflowActionMetaProvider/WorkflowActionMetaProvider';
import {
  CaseTriggerObject,
  WorkflowRunSimulateContext,
} from 'dashboard/models/workflow-run-simulated';
import TriggerFieldDefinitionPanel from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/WorkflowSimulateControlPanel/TriggerOverrideEvent/TriggerFieldDefinitionPanel';
import {
  generateSimulatedTriggerFieldOverrides,
  sanitizeFields,
} from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/WorkflowSimulateControlPanel/TriggerOverrideEvent/utils';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';
import { FieldSchema } from 'lib/fields';

interface TriggerOverrideCaseProps {
  setSimulateContext: (arg: WorkflowRunSimulateContext) => void;
  simulateContext: WorkflowRunSimulateContext;
  setTriggerFieldSchemas: (fieldSchemas: FieldSchema[]) => void;
  templateOptions: { label: string; value: string }[];
  disabled: boolean;
  requiredFields: string[];
  referencedFields: string[];
  triggerFieldDefinitionPanelOpen: boolean;
  toggleTriggerFieldDefinitionPanel: () => void;
  closeTriggerFieldDefinitionPanel: () => void;
  allowStatusSelection: boolean;
}

const TriggerOverrideCaseEvent: VFC<TriggerOverrideCaseProps> = ({
  setSimulateContext,
  simulateContext,
  setTriggerFieldSchemas,
  templateOptions,
  disabled,
  requiredFields,
  referencedFields,
  triggerFieldDefinitionPanelOpen,
  toggleTriggerFieldDefinitionPanel,
  closeTriggerFieldDefinitionPanel,
  allowStatusSelection,
}) => {
  const actionMeta = useWorkflowActionMeta();
  const [currentTemplateId, setCurrentTemplateId] = useState<string>(
    (simulateContext?.trigger?.type === 'case' && simulateContext.trigger['case-template-id']) ||
      '',
  );
  const [currentStateId, setCurrentStateId] = useState<string>(
    (simulateContext?.trigger?.type === 'case' && simulateContext.trigger['case-state-id']) || '',
  );

  const caseStateOptions = useMemo(
    () =>
      actionMeta['case-states']
        .filter((caseStateMeta) => caseStateMeta.templateToken === currentTemplateId)
        .map(({ token, name }) => ({ label: name, value: token })),
    [actionMeta, currentTemplateId],
  );
  const fieldSchemas = useMemo<FieldSchema[] | null>(() => {
    if (currentTemplateId) {
      return (
        actionMeta['case-templates'].find((template) => template.token === currentTemplateId)
          ?.fieldSchemas || null
      );
    } else {
      return [];
    }
  }, [currentTemplateId, actionMeta]);

  const handleSelectTemplate = (value: string) => {
    setCurrentTemplateId(value);
    if (value === '') {
      setSimulateContext({
        ...simulateContext,
        trigger: undefined,
      });
    } else {
      const caseTemplateMeta = actionMeta['case-templates'].find(
        (template) => template.token === value,
      );
      const templateFieldSchemas = caseTemplateMeta ? caseTemplateMeta.fieldSchemas : [];

      setSimulateContext({
        ...simulateContext,
        trigger: {
          ...simulateContext.trigger,
          'case-template-id': value,
          type: 'case',
          overrides: generateSimulatedTriggerFieldOverrides(templateFieldSchemas, simulateContext),
        },
      });
      setTriggerFieldSchemas(templateFieldSchemas);
    }
  };

  const handleSelectState = (value: string) => {
    setCurrentStateId(value);
    // (evan.qi) state select only appears when template is selected, so
    // simulateContext.trigger should already be CaseTriggerObject.
    const trigger = simulateContext.trigger as CaseTriggerObject;
    if (value === '') {
      setSimulateContext({
        ...simulateContext,
        trigger: {
          ...trigger,
          'case-state-id': null,
        },
      });
    } else {
      setSimulateContext({
        ...simulateContext,
        trigger: {
          ...trigger,
          'case-state-id': value,
        },
      });
    }
  };

  return (
    <>
      <Spacer height={1} />
      <ParamLabel labelText='Case Template' htmlFor='override-case-trigger_case__template-input' />
      <ReactSelect
        value={currentTemplateId}
        options={templateOptions}
        onChange={handleSelectTemplate}
        disabled={disabled}
        id='override-case-trigger_case__template'
        portal
        isClearable
      />
      {allowStatusSelection && currentTemplateId && (
        <>
          <Spacer height={2} />
          <Label id='case-state-selector-label' weight='bold'>
            Case State
          </Label>
          <Spacer height={1} />
          <ReactSelect
            value={currentStateId}
            options={caseStateOptions}
            onChange={handleSelectState}
            disabled={disabled}
            aria-labelledby='case-state-selector-label'
            id='override-case-trigger_case__case-state-input'
            portal
            isClearable
          />
        </>
      )}
      {simulateContext.trigger && fieldSchemas && fieldSchemas.length > 0 && (
        <>
          <Spacer height={2} />
          {triggerFieldDefinitionPanelOpen && (
            <TriggerFieldDefinitionPanel
              simulateContext={simulateContext}
              setSimulateContext={setSimulateContext}
              closePanel={closeTriggerFieldDefinitionPanel}
              fieldSchemas={fieldSchemas}
              disabled={disabled}
              triggerType='case'
              requiredFields={requiredFields}
              referencedFields={referencedFields}
            />
          )}
          <FieldsList
            fields={
              simulateContext.trigger?.overrides
                ? sanitizeFields(simulateContext.trigger.overrides)
                : {}
            }
            fieldKey='case-override-fields'
            toggleEditPanel={toggleTriggerFieldDefinitionPanel}
            tokensToDisplayValue={{}}
            editable={!disabled}
            errors={[]}
          />
        </>
      )}
    </>
  );
};

export default TriggerOverrideCaseEvent;
